// Helper to format file size in a human-readable way
import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import './TablePage.css';
import { useLocation, useHistory } from 'react-router-dom';
import { LOGGED_IN_KEY } from './LoginPage';
import { humanFileSize, getApiUrl } from './fileUtils';
import { fileKind, kindLabel, FileIcon } from './fileTypes';

// Static fallback sample rows used only when API not available
const sampleData = [
  { name: '示例文檔1', type: '音頻' },
  { name: '示例文檔2', type: '視頻' },
  { name: '示例文檔3', type: '文字' },
];

const typeMap: Record<string, string> = {
  video: '視頻',
  audio: '音頻',
  text: '文字',
  hidden: '出家師父專看，在家居士勿進'
};

const TablePage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const segments = location.pathname.split('/').filter(Boolean); // e.g. ['table','video']
  const categoryKey = segments[1];
  const category = typeMap[categoryKey];

  const [entries, setEntries] = useState<{name:string; isDir:boolean; size?:number; mtime:number;}[]>([]);
  const [pathStack, setPathStack] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [shareSuccess, setShareSuccess] = useState<string | null>(null);
  const [autoPreview, setAutoPreview] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<'audio'|'video'|'image'|'pdf'|'text'|null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  // share modal removed; share is copied silently with fallback prompt
  // /table/video, /table/audio, /table/text, /table/hidden all render this same component
  // instance (React Router doesn't remount across sibling routes), so state like pathStack
  // persists across category switches. Track the last-loaded category to force a reload
  // even when the new category's root path happens to match the stale pathStack (e.g. "").
  const loadedCategoryRef = useRef<string | undefined>(undefined);

  const load = useCallback(async (nextPath: string, nextPage = 1, nextPageSize = pageSize, updateUrl = true) => {
    if (!categoryKey) return; // wait until category known
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ category: categoryKey, p: nextPath, page: String(nextPage), pageSize: String(nextPageSize) });
      const res = await fetch(`/api/list?${params.toString()}`);
      if (res.status === 401) {
        localStorage.removeItem(LOGGED_IN_KEY);
        history.push('/login');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // If the path points to a file, return parent directory entries and request preview
      if (data && data.isFile) {
        // Derive parent dir
        const parts = (nextPath || '').split('/').filter(Boolean);
        const fileName = parts.pop() || '';
        const parent = parts.join('/');
        // Load parent directory entries by calling load recursively but avoid infinite loop
        const parentParams = new URLSearchParams({ category: categoryKey, p: parent, page: String(nextPage), pageSize: String(nextPageSize) });
        const parentRes = await fetch(`/api/list?${parentParams.toString()}`);
        const parentData = parentRes.ok ? await parentRes.json() : { entries: [] };
        setEntries(parentData.entries || []);
        setPathStack(parent);
        setTotal(parentData.total || 0);
        setPage(nextPage);
        setPageSize(nextPageSize);
        // mark file for auto preview
        setAutoPreview(parent ? `${parent}/${fileName}` : fileName);
      } else {
        setEntries(data.entries || []);
        setPathStack(nextPath);
        setTotal(data.total || 0);
        setPage(nextPage);
        setPageSize(nextPageSize);
        setAutoPreview(null);
      }
      setPathStack(nextPath);
      setTotal(data.total || 0);
      setPage(nextPage);
      setPageSize(nextPageSize);
      // Sync URL for shareable deep link
      if (updateUrl) {
        const encoded = nextPath.split('/').filter(Boolean).map(encodeURIComponent).join('/');
        const currentSegments = location.pathname.split('/').filter(Boolean).slice(2); // existing path parts after category
        const currentPathInUrl = currentSegments.join('/');
        if (encoded !== currentPathInUrl) {
          const newUrl = encoded ? `/table/${categoryKey}/${encoded}` : `/table/${categoryKey}`;
          history.push(newUrl);
        }
      }
    } catch (e:any) {
      setError(e.message);
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [categoryKey, pageSize, history, location.pathname]);

  // Initial load & respond to URL path changes (deep links)
  useEffect(() => {
    if (!categoryKey) return;
    if (categoryKey === 'hidden' && !localStorage.getItem(LOGGED_IN_KEY)) {
      history.push('/login');
      return;
    }
    const segments = location.pathname.split('/').filter(Boolean);
    const raw = segments.slice(2); // after /table/<category>
    const initialPath = raw.map(decodeURIComponent).join('/');
    const categoryChanged = loadedCategoryRef.current !== categoryKey;
    // Load if the category changed, the path differs, or nothing loaded yet
    if (categoryChanged || initialPath !== pathStack || entries.length === 0) {
      loadedCategoryRef.current = categoryKey;
      load(initialPath, 1, pageSize, false); // don't update URL when reading it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, location.pathname, entries.length]);

  // When server indicates a file was requested, autoPreview will be set to the file path
  // Open the preview endpoint in a new tab and then clear the flag
  useEffect(() => {
    if (autoPreview && categoryKey) {
      const ext = (autoPreview.split('.').pop() || '').toLowerCase();
      const url = `/api/preview?category=${categoryKey}&p=${encodeURIComponent(autoPreview)}`;
      const isVideo = ['mp4', 'mov', 'webm', 'mkv', 'avi', 'mpeg', 'mpg'].includes(ext);
      const isAudio = ['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a'].includes(ext);
      const isImage = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
      const isPdf = ext === 'pdf';
      const isText = ['txt', 'md', 'csv', 'log', 'json', 'xml', 'html'].includes(ext);
      const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'].includes(ext);

      if (isVideo) {
        setPreviewKind('video');
        setPreviewSrc(url);
      } else if (isAudio) {
        setPreviewKind('audio');
        setPreviewSrc(url);
      } else if (isImage) {
        setPreviewKind('image');
        setPreviewSrc(url);
      } else if (isPdf) {
        setPreviewKind('pdf');
        setPreviewSrc(url);
      } else if (isText) {
        setPreviewKind('text');
        setPreviewSrc(url);
        // fetch text content (limit to first 200KB)
        (async () => {
          try {
            const r = await fetch(url);
            if (!r.ok) throw new Error('Failed to load');
            const txt = await r.text();
            setTextPreview(txt.slice(0, 200 * 1024));
          } catch (e) {
            setTextPreview('無法預覽此文件');
          }
        })();
      } else if (isOffice) {
        // show download link in modal
        setPreviewKind(null);
        setPreviewSrc(url);
      } else {
        // other: open in new tab
        window.open(url, '_blank', 'noopener');
        setAutoPreview(null);
        return undefined;
      }

  // clear autoPreview flag after small delay
      const t = setTimeout(() => setAutoPreview(null), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [autoPreview, categoryKey]);

  const closePreview = () => {
    setPreviewSrc(null);
    setPreviewKind(null);
    setTextPreview(null);
  };

  // Update page title with brand + category
  useEffect(() => {
    if (category) {
      document.title = `普熏十方 - ${category}列表`;
    } else {
      document.title = '普熏十方';
    }
  }, [category]);

  const goInto = (name: string) => load(pathStack ? pathStack + '/' + name : name, 1, pageSize);
  const goUp = () => {
    if (!pathStack) return;
    const parts = pathStack.split('/');
    parts.pop();
    load(parts.join('/'), 1, pageSize);
  };

  // Normalize a displayed filename to the actual filename.
  // If a display name contains extra labels like ' 分享: <filename>', extract the first
  // file-like token (name + extension) and return that. Falls back to last path segment.
  const normalizeFileName = (displayName: string) => {
    if (!displayName) return displayName;
  // Find all filename-like tokens and pick the first (usually the clean name)
  const re = /[\w\- \u4e00-\u9fff.:]+?\.(mp4|mov|webm|mkv|avi|mp3|wav|pdf|txt|docx?|xlsx?|pptx?|jpg|jpeg|png|gif|csv|md|json)/gi;
  const matches = Array.from(displayName.matchAll(re)).map(m => m[0].trim());
  if (matches.length) return matches[0];
    // fallback: use last path segment if given a path
    const parts = displayName.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : displayName;
  };

  const handleShare = (filePath: string, fileName: string, isFile = false) => {
    // Normalize file name to avoid labels like ' 分享: ...' being included in the shared path
    const cleanName = normalizeFileName(fileName);
    // Build path segments; for directories we keep the raw segments, for files
    // replace the last segment with the normalized filename to avoid UI labels leaking into the URL.
    const parts = filePath.split('/');
    if (parts.length && isFile) {
      const last = parts[parts.length - 1];
      if (last === fileName || last !== cleanName) parts[parts.length - 1] = cleanName;
    }
    const normalizedPath = parts.join('/');
    // Prefer sharing via the server-side /share route so crawlers (WeChat) read OG tags,
    // then users are redirected to the preview endpoint. Use backend origin to avoid dev-proxy issues.
  // share URL is relative; no backend origin needed
  // Encode path segments individually to avoid encoding slashes into the filename
  const segmentsToEncode = normalizedPath.split('/').filter(Boolean).map(s => encodeURIComponent(s)).join('/');
  // Server-side share route path (relative). We'll build an absolute URL when copying
  // so chat apps and mobile browsers treat it as a link.
  const shareUrl = `/share/${categoryKey}/${segmentsToEncode}`;
  let shareUrlAbsolute = shareUrl;
  try {
    const loc = window.location;
    shareUrlAbsolute = `${loc.protocol}//${loc.hostname}${loc.port ? ':' + loc.port : ''}${shareUrl}`;
  } catch (e) {
    // fallback: keep relative
  }
    
  // Update Open Graph meta tags for this specific share
    const ogTitle = `${fileName} - ${category || '普熏十方'}`;
    const ogDesc = `分享: ${fileName}`;
    
    // Set/update meta tags
    const setMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
  setMetaTag('og:title', ogTitle);
  setMetaTag('og:description', ogDesc);
  setMetaTag('og:url', shareUrlAbsolute);

  // Prefer the Web Share API so mobile/web shows the native share sheet (asks where to share).
  // If unavailable, fall back to copying the absolute URL into clipboard.
  (async () => {
    try {
      if (navigator && (navigator as any).share) {
        // Use native share sheet when available (mobile browsers / supporting desktops)
        try {
          await (navigator as any).share({
            title: ogTitle,
            text: ogDesc,
            url: shareUrlAbsolute,
          });
          // share completed or dismissed by user; show a short success indicator
          setShareSuccess(filePath);
          setTimeout(() => setShareSuccess(null), 2000);
          return;
        } catch (err) {
          // share API rejected (user cancelled or error) -> fall through to clipboard fallback
          console.info('Native share failed or cancelled', err);
        }
      }

      // Clipboard API
      if (navigator && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrlAbsolute);
        setShareSuccess(filePath);
        setTimeout(() => setShareSuccess(null), 2000);
        return;
      }
    } catch (e) {
      console.error('Clipboard API / share failed', e);
    }

    // execCommand fallback for older browsers
    try {
      const ta = document.createElement('textarea');
      ta.value = shareUrlAbsolute;
      // Prevent layout shifts
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        setShareSuccess(filePath);
        setTimeout(() => setShareSuccess(null), 2000);
        return;
      }
    } catch (e) {
      console.error('execCommand copy failed', e);
    }

    // If none of the methods worked, fall back to a non-blocking indicator in the UI (no prompt).
    console.warn('Unable to share or copy share URL programmatically; user may need to copy manually:', shareUrlAbsolute);
  })();

    }; // end handleShare

  const handlePageChange = (newPage: number) => {
    load(pathStack, newPage, pageSize);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    load(pathStack, 1, newSize);
  };

  const filtered = useMemo(() => {
    if (!category) return sampleData; // fallback only on /table
  // Sort by name (directories and files mixed)
  return [...entries].sort((a, b) => a.name.localeCompare(b.name, 'zh-Hans-CN', { numeric: true }));
  }, [category, entries]);

  return (
  <div className="table-page-responsive">
      <h2 style={{ textAlign: 'center', color: 'var(--main-coffee)', fontWeight: 600, letterSpacing: '0.15em', marginBottom: '20px' }}>
        普熏十方{category ? ` - ${category}列表` : ''}
      </h2>
      {categoryKey && (
        <div style={{marginBottom:'14px', display:'flex', flexDirection:'column', gap:'8px', alignItems:'flex-start', flexWrap:'wrap'}}>
          <span style={{fontWeight:600, color:'var(--main-coffee)', background:'var(--table-chip-bg,#f7f2ee)', padding:'6px 12px', borderRadius: '6px', transition:'background .35s'}}>
            {(() => {
              const rootMap: Record<string, string> = { audio: '音頻', video: '視頻', text: '文字' };
              const rootLabel = categoryKey && rootMap.hasOwnProperty(categoryKey) ? rootMap[categoryKey] : categoryKey;
              const parts = [rootLabel, ...((pathStack && pathStack.length) ? pathStack.split('/') : [])];
              return parts.filter(Boolean).map((part, idx, arr) => {
                const isLast = idx === arr.length - 1;
                // Build the path up to this segment
                const subPath = idx === 0 ? '' : arr.slice(1, idx + 1).join('/');
                return (
                  <span key={idx}>
                    <span
                      style={{
                        cursor: isLast ? 'default' : 'pointer',
                        color: isLast ? 'var(--main-coffee-light)' : 'var(--main-coffee)',
                        textDecoration: 'none',
                        fontWeight: isLast ? 600 : 500
                      }}
                      onClick={() => {
                        if (!isLast) load(subPath);
                      }}
                    >
                      {part}
                    </span>
                    {!isLast && ' -> '}
                  </span>
                );
              });
            })()}
          </span>
          <button disabled={!pathStack || loading} onClick={goUp} style={{padding:'6px 12px', border:'1px solid var(--main-coffee)', background:'#fff', cursor: pathStack? 'pointer':'not-allowed', borderRadius:6}}>上級目錄</button>
        </div>
      )}
  <div className="table-responsive-wrapper">
  {/* (moved) pagination now rendered after table */}
        {loading && <div className="table-loading-overlay">載入中...</div>}
        <table className="responsive-table file-table">
          <thead>
            <tr>
              <th>名稱</th>
              <th>類型</th>
              <th>大小</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row: any, idx) => {
              const clickable = category && row.isDir;
              const isFile = !row.isDir;
              // Build file path for actions
              const filePath = pathStack ? `${pathStack}/${row.name}` : row.name;
              const kind = fileKind(row.name, row.isDir);
              const icon = <FileIcon kind={kind} />;
              return (
                <tr key={idx} className={row.isDir ? 'dir-row' : 'file-row'} style={{ cursor: clickable ? 'pointer' : 'default' }} onClick={() => clickable && goInto(row.name)}>
                  <td data-label="名稱">
                    <span className="file-name-cell">
                      {icon}
                      <span>{row.name}</span>
                    </span>
                  </td>
                  <td data-label="類型">{category ? kindLabel(kind) : row.type}</td>
                  <td data-label="大小">{row.size !== undefined ? humanFileSize(row.size) : ''}</td>
                  <td data-label="操作">{(isFile || row.isDir) && (
                    <span style={{ display: 'inline-flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
                      {/* Share icon */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShare(filePath, row.name, isFile); }}
                        title="分享鏈接"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--main-coffee)',
                          fontSize: 18,
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'color 0.3s'
                        }}
                      >
                        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </button>
                      {shareSuccess === filePath && <span style={{ fontSize: 12, color: 'var(--main-green, #4caf50)' }}>已複製</span>}

                      {isFile && (
                        <>
                          {/* Watch online icon (eye) */}
                          <a
                            href={getApiUrl('/api/preview', { category: categoryKey, p: filePath })}
                            title="在線觀看"
                            style={{ color: 'var(--main-coffee)', fontSize: 18, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                          >
                            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          </a>
                          {/* Download icon */}
                          <a
                            href={getApiUrl('/api/download', { category: categoryKey, p: filePath })}
                            title="下載"
                            style={{ color: 'var(--main-coffee)', fontSize: 18, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={e => {
                              e.stopPropagation();
                              const url = `/api/download?category=${categoryKey}&p=${encodeURIComponent(filePath)}`;
                              console.log('Download URL:', url);
                            }}
                            download
                          >
                            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          </a>
                        </>
                      )}
                    </span>
                  )}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: 'var(--main-coffee)' }}>暫無數據</td>
              </tr>
            )}
          </tbody>
        </table>
        {category && total > 0 && (
          <div className="table-pagination" aria-label="分頁控制">
            <div className="table-pagination-left">
              <label style={{display:'flex',alignItems:'center',gap:6}}>每頁顯示
                <select value={pageSize} onChange={handlePageSizeChange}>
                  {[10,20,50,100].map(size => <option key={size} value={size}>{size}</option>)}
                </select>
                條
              </label>
            </div>
            <div className="table-pagination-center">
              <span>第 {page} / {Math.max(1, Math.ceil(total / pageSize))} 頁</span>
            </div>
            <div className="table-pagination-right">
              <button onClick={() => handlePageChange(1)} disabled={page===1 || loading}>首頁</button>
              <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || loading}>上一頁</button>
              <button onClick={() => handlePageChange(page + 1)} disabled={page >= Math.ceil(total / pageSize) || loading}>下一頁</button>
              <button onClick={() => handlePageChange(Math.ceil(total / pageSize))} disabled={page >= Math.ceil(total / pageSize) || loading}>末頁</button>
              <span className="table-pagination-total">共 {total} 條</span>
            </div>
          </div>
        )}
      </div>
      {error && <div style={{marginTop:16, color:'red'}}>錯誤: {error}</div>}
      {/* In-page media preview modal */}
      {previewSrc && (
        <div className="preview-modal" role="dialog" aria-modal="true" style={{position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.5)',zIndex:2000}} onClick={closePreview}>
          <div style={{background:'#fff',padding:12,borderRadius:8,maxWidth:'90%',maxHeight:'90%',display:'flex',flexDirection:'column',gap:8}} onClick={e => e.stopPropagation()}>
            <button onClick={closePreview} style={{alignSelf:'flex-end',background:'transparent',border:'none',fontSize:18,cursor:'pointer'}}>✕</button>
            {previewKind === 'video' && (
              <video src={previewSrc || undefined} controls autoPlay style={{maxWidth:'80vw', maxHeight:'70vh'}} />
            )}
            {previewKind === 'audio' && (
              <audio src={previewSrc || undefined} controls autoPlay style={{width:'60vw'}} />
            )}
            {previewKind === 'image' && previewSrc && (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={previewSrc} alt="image preview" style={{maxWidth:'80vw', maxHeight:'70vh'}} />
            )}
            {previewKind === 'pdf' && previewSrc && (
              <iframe src={previewSrc} title="pdf-preview" style={{width:'80vw', height:'70vh', border:'none'}} />
            )}
            {previewKind === 'text' && (
              <div style={{maxWidth:'80vw', maxHeight:'70vh', overflow:'auto', whiteSpace:'pre-wrap'}}>
                {textPreview || '載入中...'}
              </div>
            )}
            {previewKind === null && previewSrc && (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <a href={previewSrc} target="_blank" rel="noopener noreferrer">下載 / 打開文件</a>
                <span style={{fontSize:12,color:'#666'}}>Office 類型文件無法直接內嵌預覽</span>
              </div>
            )}
          </div>
        </div>
      )}
  {/* share modal removed - silent copy flow is used */}
    </div>
  );
};

export default TablePage;
